import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import facilityThemeService from "../../services/facilityThemeService";
import { hexToHsl, hslToHex } from "../../utils/colorConversion";
import { DEFAULT_THEME_CONFIG } from "../../constants/facility.constants";
import { deepEqual } from "../../../../helpers/helpers";

const toHexConfig = (config) => config.map((item) => ({ ...item, color_value: hslToHex(item.color_value) }));

const FacilityThemeTab = ({ facilityNumber, onCancel }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [themeConfig, setThemeConfig] = useState([]);
  const [originalThemeConfig, setOriginalThemeConfig] = useState([]);
  const [isFirstSave, setIsFirstSave] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const colorInputRefs = useRef({});

  useEffect(() => {
    if (!facilityNumber) return;
    setIsLoading(true);
    facilityThemeService
      .getFacilityThemeById(facilityNumber)
      .then((data) => {
        if (!data?.theme_config) {
          setIsFirstSave(true);
          const converted = toHexConfig(DEFAULT_THEME_CONFIG);
          setThemeConfig(converted);
          setOriginalThemeConfig(converted);
          setHistory([]);
        } else {
          setIsFirstSave(false);
          const converted = toHexConfig(data.theme_config);
          setThemeConfig(converted);
          setOriginalThemeConfig(converted);
          setHistory(data.history || []);
        }
      })
      .catch((error) => {
        console.error("Error fetching facility theme:", error);
        toast.error("Failed to load facility theme.");
      })
      .finally(() => setIsLoading(false));
  }, [facilityNumber]);

  const hasChanges = !deepEqual(themeConfig, originalThemeConfig);

  const handleColorChange = (index, value) => {
    setThemeConfig((prev) => prev.map((item, i) => (i === index ? { ...item, color_value: value } : item)));
  };

  const handleResetToDefault = () => {
    setThemeConfig(toHexConfig(DEFAULT_THEME_CONFIG));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const dataToSave = themeConfig.map((item) => ({ ...item, color_value: hexToHsl(item.color_value) }));
      const response = isFirstSave
        ? await facilityThemeService.createFacilityTheme({
            facility_id: parseInt(facilityNumber, 10),
            theme_config: dataToSave,
          })
        : await facilityThemeService.updateFacilityTheme(facilityNumber, { theme_config: dataToSave });

      toast.success("Theme saved!");
      setIsFirstSave(false);
      setHistory(response.history || []);
      const converted = toHexConfig(response.theme_config);
      setThemeConfig(converted);
      setOriginalThemeConfig(converted);
    } catch (error) {
      console.error("Error saving theme config:", error);
      toast.error("Failed to save theme.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevert = (config) => {
    setThemeConfig(toHexConfig(config));
    toast.info("Reverted to selected version. Don't forget to save!");
  };

  if (isLoading) {
    return <div className="py-16 text-center text-gray-500">Loading theme configuration...</div>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-100 p-5">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Theme</h3>
          <p className="mt-0.5 text-xs text-gray-400">Colors applied across this facility's workspace</p>
        </div>
        <button type="button" onClick={handleResetToDefault} className="text-sm font-semibold text-[#2B3674] hover:underline">
          Reset to default
        </button>
      </div>

      <div className="rounded-xl border border-gray-100 p-5">
        <div className="mb-3 grid grid-cols-[1fr,1fr,auto] gap-4 text-xs font-semibold uppercase tracking-wide text-gray-400">
          <span>Component</span>
          <span>Hex</span>
          <span>Preview</span>
        </div>
        <div className="space-y-3">
          {themeConfig.map((color, index) => (
            <div key={color.color_key} className="grid grid-cols-[1fr,1fr,auto] items-center gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-800">{color.display_name}</p>
                {color.description && <p className="text-xs text-gray-400">{color.description}</p>}
              </div>
              <input
                type="text"
                value={color.color_value}
                onChange={(e) => handleColorChange(index, e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={color.color_value}
                  ref={(el) => (colorInputRefs.current[index] = el)}
                  onChange={(e) => handleColorChange(index, e.target.value)}
                  className="h-0 w-0 opacity-0"
                />
                <button
                  type="button"
                  onClick={() => colorInputRefs.current[index]?.click()}
                  className="h-9 w-16 rounded-lg border border-gray-200"
                  style={{ backgroundColor: color.color_value }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl bg-gray-50 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Live preview</p>
          <div className="flex flex-wrap gap-2">
            {themeConfig.map((color) => (
              <span
                key={color.color_key}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white"
                style={{ backgroundColor: color.color_value }}
              >
                {color.display_name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div className="mt-4">
          <button type="button" onClick={() => setShowHistory((prev) => !prev)} className="text-sm font-semibold text-[#2B3674] hover:underline">
            {showHistory ? "Hide history" : "View history"}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {history.map((entry, idx) => (
                <div key={idx} className="rounded-xl border border-gray-100 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Updated at: {new Date(`${entry.updated_at}Z`).toLocaleString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRevert(entry.theme_config)}
                      className="text-xs font-semibold text-[#2B3674] hover:underline"
                    >
                      Revert to this version
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {entry.theme_config.map((color) => (
                      <span
                        key={color.color_key}
                        className="rounded-lg px-2.5 py-1 text-xs font-semibold text-white"
                        style={{ backgroundColor: hslToHex(color.color_value) }}
                      >
                        {color.display_name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-100 pt-5">
        <button type="button" onClick={onCancel} className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="rounded-xl bg-[#2B3674] px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save theme"}
        </button>
      </div>
    </div>
  );
};

export default FacilityThemeTab;
